/**
 * AudienceLayout — Shared layout for the 3 audience-specific landing pages
 * (Company / Gym / Individual). Shows a hero with dominant color, sections
 * for "how it works", benefits, differentials, and a strong final CTA.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const colorMap = {
  blue: {
    text: 'text-blue-600', bg: 'bg-blue-600', hover: 'hover:bg-blue-700',
    softBg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-200',
    grad: 'from-blue-600 to-blue-500',
  },
  orange: {
    text: 'text-orange-500', bg: 'bg-orange-500', hover: 'hover:bg-orange-600',
    softBg: 'bg-orange-50', border: 'border-orange-200', ring: 'ring-orange-200',
    grad: 'from-orange-500 to-orange-400',
  },
  emerald: {
    text: 'text-emerald-600', bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700',
    softBg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-200',
    grad: 'from-emerald-600 to-emerald-500',
  },
};

const AudienceLayout = ({
  color = 'blue',
  Icon,
  eyebrow,
  title,
  subtitle,
  heroImage,
  onPrimary,
  onSecondary,
  primaryCtaLabel = 'CRIAR CONTA AGORA',
  secondaryCtaLabel = 'Conhecer a Plataforma',
  steps = [],
  benefits = [],
  differentials = [],
  extraSection = null,
  finalCtaTitle,
  finalCtaSubtitle,
  registrationForm = null,
  testIdPrefix = 'audience',
}) => {
  const c = colorMap[color] || colorMap.blue;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`${testIdPrefix}-page`}>
      {/* Header */}
      <header className="glass fixed top-0 w-full z-50 border-b border-slate-200/50 bg-white/80 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-12 py-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid={`${testIdPrefix}-back`}>
            <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-900" />
            <Activity className="w-6 h-6 text-blue-600" />
            <span className="text-slate-900 font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital Performance</span>
          </button>
          <Button size="sm" className={`${c.bg} ${c.hover} text-white text-xs sm:text-sm`} onClick={onPrimary} data-testid={`${testIdPrefix}-header-cta`}>
            {primaryCtaLabel}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            {eyebrow && (
              <div className={`inline-flex items-center gap-2 ${c.softBg} ${c.text} px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4`}>
                <Icon className="w-4 h-4" /> {eyebrow}
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-none mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {title}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-xl">
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className={`${c.bg} ${c.hover} text-white font-bold`} onClick={onPrimary} data-testid={`${testIdPrefix}-hero-cta-primary`}>
                {primaryCtaLabel}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              {onSecondary && (
                <Button size="lg" variant="outline" className={`border-2 ${c.border} ${c.text}`} onClick={onSecondary} data-testid={`${testIdPrefix}-hero-cta-secondary`}>
                  {secondaryCtaLabel}
                </Button>
              )}
            </div>
          </div>
          {heroImage && (
            <div className="relative">
              <img src={heroImage} alt={title} className="w-full h-auto rounded-3xl shadow-2xl" loading="eager" />
              <div className={`absolute -bottom-4 -right-4 w-24 h-24 ${c.softBg} rounded-2xl -z-10`}></div>
            </div>
          )}
        </div>
      </section>

      {/* Como Funciona */}
      {steps.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-12 bg-white" data-testid={`${testIdPrefix}-how-it-works`}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className={`text-sm font-bold uppercase tracking-widest ${c.text} mb-2`}>PROCESSO SIMPLES</p>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Como Funciona</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {steps.map((step, i) => (
                <div key={`step-${step.title || i}`} className="flex gap-4 p-5 bg-slate-50 rounded-2xl">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${c.bg} text-white font-bold flex items-center justify-center`}>
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 mb-1">{step.title}</p>
                    {step.desc && <p className="text-sm text-slate-600">{step.desc}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefícios */}
      {benefits.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-12 bg-slate-50" data-testid={`${testIdPrefix}-benefits`}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className={`text-sm font-bold uppercase tracking-widest ${c.text} mb-2`}>VANTAGENS</p>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Benefícios</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {benefits.map((b) => (
                <Card key={b.title} className="border-slate-100 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className={`w-11 h-11 ${c.softBg} rounded-xl flex items-center justify-center mb-3`}>
                      <b.icon className={`w-5 h-5 ${c.text}`} />
                    </div>
                    <p className="font-bold text-slate-900 text-sm leading-tight">{b.title}</p>
                    {b.desc && <p className="text-xs text-slate-500 mt-1">{b.desc}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Diferenciais */}
      {differentials.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-12 bg-white" data-testid={`${testIdPrefix}-diff`}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className={`text-sm font-bold uppercase tracking-widest ${c.text} mb-2`}>POR QUE ESCOLHER</p>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Nossos Diferenciais</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {differentials.map((d) => (
                <div key={d} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <div className={`flex-shrink-0 w-7 h-7 ${c.softBg} rounded-full flex items-center justify-center`}>
                    <Check className={`w-4 h-4 ${c.text}`} />
                  </div>
                  <p className="text-slate-700 font-medium text-sm sm:text-base">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {extraSection}

      {/* CTA Final */}
      <section className={`py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-gradient-to-br ${c.grad}`} data-testid={`${testIdPrefix}-final-cta`}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {finalCtaTitle}
          </h2>
          {finalCtaSubtitle && <p className="text-lg text-white/90 mb-8 leading-relaxed">{finalCtaSubtitle}</p>}
          <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 font-black tracking-wide px-8" onClick={onPrimary} data-testid={`${testIdPrefix}-final-cta-btn`}>
            {primaryCtaLabel}
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </section>

      {registrationForm}

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-900 text-center">
        <p className="text-xs text-slate-500">2026 © Vital Performance Hub — Todos os direitos reservados</p>
      </footer>
    </div>
  );
};

export default AudienceLayout;
